import { doc, setDoc, getDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Service for generating and managing brand sign-in tokens
 */
class BrandSignInTokenService {
  /**
   * Generate a unique token
   * @returns {string} Unique token string
   */
  static generateToken() {
    // Use crypto.randomUUID if available, otherwise generate a random string
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback: generate a random string
    return 'brand_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Generate a sign-in token for a brand
   * @param {string} brandUserId - The brand user ID
   * @param {string} brandEmail - The brand email
   * @param {string} brandName - The brand name
   * @returns {Promise<{token: string, link: string}>} Token and sign-in link
   */
  static async generateSignInToken(brandUserId, brandEmail, brandName) {
    try {
      // Generate a unique token
      const token = this.generateToken();
      
      // Create expiration date (7 days from now)
      const expiresAt = Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
      
      // Store token in Firestore
      const tokenRef = doc(db, 'brand_signin_tokens', token);
      await setDoc(tokenRef, {
        brandUserId,
        brandEmail,
        brandName,
        token,
        status: 'pending', // 'pending', 'used', 'expired'
        createdAt: serverTimestamp(),
        expiresAt,
        usedAt: null
      });
      
      // Generate sign-in link
      const baseUrl = window.location.origin;
      const signInLink = `${baseUrl}/brand-signin?token=${token}`;
      
      return {
        token,
        link: signInLink,
        expiresAt: expiresAt.toDate()
      };
    } catch (error) {
      console.error('Error generating brand sign-in token:', error);
      throw new Error('Failed to generate sign-in token');
    }
  }
  
  /**
   * Validate a token (sign-in or sign-up)
   * @param {string} token - The token to validate
   * @returns {Promise<{valid: boolean, data: object|null, tokenType: string}>} Validation result
   */
  static async validateToken(token) {
    try {
      // First check for sign-up token (for new brands)
      const signupTokenRef = doc(db, 'brand_signup_tokens', token);
      const signupTokenDoc = await getDoc(signupTokenRef);
      
      if (signupTokenDoc.exists()) {
        const tokenData = signupTokenDoc.data();
        
        // Check if token is expired
        if (tokenData.expiresAt && tokenData.expiresAt.toDate() < new Date()) {
          await setDoc(signupTokenRef, { status: 'expired' }, { merge: true });
          return { valid: false, data: null, error: 'Token has expired', tokenType: 'signup' };
        }
        
        // Check if token has already been used
        if (tokenData.status === 'used') {
          return { valid: false, data: null, error: 'Token has already been used', tokenType: 'signup' };
        }
        
        return { 
          valid: true, 
          data: {
            brandEmail: tokenData.brandEmail,
            brandName: tokenData.brandName
          },
          tokenType: 'signup'
        };
      }
      
      // Check for sign-in token (for existing brands)
      const signinTokenRef = doc(db, 'brand_signin_tokens', token);
      const signinTokenDoc = await getDoc(signinTokenRef);
      
      if (!signinTokenDoc.exists()) {
        return { valid: false, data: null, error: 'Invalid token', tokenType: null };
      }
      
      const tokenData = signinTokenDoc.data();
      
      // Check if token is expired
      if (tokenData.expiresAt && tokenData.expiresAt.toDate() < new Date()) {
        await setDoc(signinTokenRef, { status: 'expired' }, { merge: true });
        return { valid: false, data: null, error: 'Token has expired', tokenType: 'signin' };
      }
      
      // Check if token has already been used
      if (tokenData.status === 'used') {
        return { valid: false, data: null, error: 'Token has already been used', tokenType: 'signin' };
      }
      
      return { 
        valid: true, 
        data: {
          brandUserId: tokenData.brandUserId,
          brandEmail: tokenData.brandEmail,
          brandName: tokenData.brandName
        },
        tokenType: 'signin'
      };
    } catch (error) {
      console.error('Error validating token:', error);
      return { valid: false, data: null, error: 'Failed to validate token', tokenType: null };
    }
  }
  
  /**
   * Mark a token as used
   * @param {string} token - The token to mark as used
   * @param {string} tokenType - 'signin' or 'signup'
   */
  static async markTokenAsUsed(token, tokenType = 'signin') {
    try {
      const collectionName = tokenType === 'signup' ? 'brand_signup_tokens' : 'brand_signin_tokens';
      const tokenRef = doc(db, collectionName, token);
      await setDoc(tokenRef, {
        status: 'used',
        usedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error marking token as used:', error);
      throw new Error('Failed to mark token as used');
    }
  }
}

export default BrandSignInTokenService;

